// ===============================================================
// 💸 WithdrawRequestPage — LIVORA (EARNINGS1 ALIGNED)
// path: src/components/Earnings/WithdrawRequestPage.js
// ===============================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFunctions, httpsCallable } from "firebase/functions";

import TopBar2 from "../common/TopBar2";
import "./WithdrawRequestPage.css";
import { formatNumberEn, normalizeDigits } from "../../utils/digits";
import { ENABLE_REAL_MONEY_PAYOUTS } from "../../config/featureFlags";

const TOKENS_PER_USD_PREVIEW = 100;

const PAYOUT_COUNTRY_OPTIONS = [
  { code: "AE", label: "United Arab Emirates" },
  { code: "SA", label: "Saudi Arabia" },
  { code: "EG", label: "Egypt" },
  { code: "JO", label: "Jordan" },
  { code: "LB", label: "Lebanon" },
  { code: "MA", label: "Morocco" },
  { code: "TN", label: "Tunisia" },
  { code: "DZ", label: "Algeria" },
  { code: "IQ", label: "Iraq" },
  { code: "KW", label: "Kuwait" },
  { code: "QA", label: "Qatar" },
  { code: "BH", label: "Bahrain" },
  { code: "OM", label: "Oman" },
  { code: "TR", label: "Turkey" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "GB", label: "United Kingdom" },
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "AT", label: "Austria" },
  { code: "CH", label: "Switzerland" },
  { code: "NL", label: "Netherlands" },
  { code: "IT", label: "Italy" },
  { code: "ES", label: "Spain" },
];

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatNumber(value) {
  return formatNumberEn(toNumber(value));
}

function formatMoney(value, fractionDigits = 2) {
  return formatNumberEn(toNumber(value), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export default function WithdrawRequestPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [amountUsd, setAmountUsd] = useState("");
  const [method, setMethod] = useState("crypto");
  const [noteFromUser, setNoteFromUser] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [eligibility, setEligibility] = useState({
    loading: true,
    eligible: false,
    reason: "",
    minWithdrawTokens: 1000,
    minWithdrawUsdPreview: 10,
    availableTokens: 0,
    availableUsdPreview: 0,
    verificationStatus: "none",
    withdrawEligibilityStatus: "none",
    methods: ["crypto", "bank", "paypal"],
    crypto: {
      assets: ["USDT"],
      networks: ["TRC20"],
    },
  });

  const [cryptoData, setCryptoData] = useState({
    asset: "USDT",
    network: "TRC20",
    walletAddress: "",
  });

  const [paypalEmail, setPaypalEmail] = useState("");

  const [bankData, setBankData] = useState({
    accountHolderName: "",
    bankName: "",
    iban: "",
    country: "",
    bic: "",
  });

  const [identityForm, setIdentityForm] = useState({
    firstName: "",
    lastName: "",
    payoutCountry: "",
    payoutOwnerConfirmed: false,
    cryptoIrreversibleAcknowledged: false,
  });

  const functions = useMemo(() => getFunctions(undefined, "us-central1"), []);

  const getWithdrawEligibility = useMemo(
    () => httpsCallable(functions, "getWithdrawEligibility"),
    [functions]
  );

  const createWithdrawRequest = useMemo(
    () => httpsCallable(functions, "createWithdrawRequest"),
    [functions]
  );

  useEffect(() => {
    let alive = true;

    const loadEligibility = async () => {
      // Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: must never call
      // getWithdrawEligibility while real-money payouts are disabled -
      // resolve locally instead. The backend would return the same
      // disabled shape anyway (defense in depth), but the requirement is
      // that this client not even place the call.
      if (!ENABLE_REAL_MONEY_PAYOUTS) {
        if (!alive) return;
        setEligibility({
          loading: false,
          eligible: false,
          reason: t("withdraw.validation.payoutsDisabled", {
            defaultValue: "Real-money withdrawal is currently unavailable.",
          }),
          minWithdrawTokens: 1000,
          minWithdrawUsdPreview: 0,
          availableTokens: 0,
          availableUsdPreview: 0,
          verificationStatus: "none",
          withdrawEligibilityStatus: "none",
          methods: [],
          crypto: { assets: [], networks: [] },
        });
        return;
      }

      try {
        setEligibility((prev) => ({ ...prev, loading: true }));

        const res = await getWithdrawEligibility();
        const data = res?.data || {};

        if (!alive) return;

        const methods = Array.isArray(data.methods)
          ? data.methods
          : ["crypto", "bank", "paypal"];

        setEligibility({
          loading: false,
          eligible: Boolean(data.eligible),
          reason: String(data.reason || ""),
          minWithdrawTokens: toNumber(data.minWithdrawTokens, 1000),
          minWithdrawUsdPreview: toNumber(data.minWithdrawUsdPreview, 10),
          availableTokens: toNumber(data.availableTokens, 0),
          availableUsdPreview: toNumber(data.availableUsdPreview, 0),
          verificationStatus: String(data.verificationStatus || "none")
            .trim()
            .toLowerCase(),
          withdrawEligibilityStatus: String(
            data.withdrawEligibilityStatus || "none"
          )
            .trim()
            .toLowerCase(),
          methods,
          crypto: {
            assets: Array.isArray(data?.crypto?.assets)
              ? data.crypto.assets
              : ["USDT"],
            networks: Array.isArray(data?.crypto?.networks)
              ? data.crypto.networks
              : ["TRC20"],
          },
          withdrawIdentitySummary:
            data?.withdrawIdentitySummary &&
            typeof data.withdrawIdentitySummary === "object"
              ? data.withdrawIdentitySummary
              : null,
        });

        const summary =
          data?.withdrawIdentitySummary &&
          typeof data.withdrawIdentitySummary === "object"
            ? data.withdrawIdentitySummary
            : null;

        if (summary) {
          setIdentityForm((prev) => ({
            ...prev,
            firstName: String(summary.firstName || prev.firstName || "").trim(),
            lastName: String(summary.lastName || prev.lastName || "").trim(),
            payoutCountry: String(
              summary.payoutCountry || prev.payoutCountry || ""
            )
              .trim()
              .toUpperCase(),
          }));
        }

        if (!methods.includes(method)) {
          setMethod(methods[0] || "crypto");
        }
      } catch (err) {
        console.error("getWithdrawEligibility failed:", err);

        if (!alive) return;

        setEligibility({
          loading: false,
          eligible: false,
          reason: t("withdraw.validation.eligibilityError", {
            defaultValue: "تعذر التحقق من حالة السحب الآن",
          }),
          minWithdrawTokens: 1000,
          minWithdrawUsdPreview: 10,
          availableTokens: 0,
          availableUsdPreview: 0,
          verificationStatus: "none",
          withdrawEligibilityStatus: "none",
          methods: ["crypto", "bank", "paypal"],
          crypto: {
            assets: ["USDT"],
            networks: ["TRC20"],
          },
        });
      }
    };

    loadEligibility();

    return () => {
      alive = false;
    };
  }, [getWithdrawEligibility, method, t]);

  useEffect(() => {
    if (!toast) return;

    const timer = setTimeout(() => {
      setToast("");
    }, 3500);

    return () => clearTimeout(timer);
  }, [toast]);

  const parsedAmountUsd = Number(amountUsd || 0);
  const requestedUsd = Number.isFinite(parsedAmountUsd)
    ? Math.max(0, parsedAmountUsd)
    : 0;

  const requestedTokens = Math.max(
    0,
    Math.floor(requestedUsd * TOKENS_PER_USD_PREVIEW)
  );

  const remainingTokens = Math.max(
    0,
    toNumber(eligibility.availableTokens) - requestedTokens
  );

  const remainingUsdPreview = Number(
    (remainingTokens / TOKENS_PER_USD_PREVIEW).toFixed(2)
  );

  const availableMethods = eligibility.methods || [];
  const availableAssets = eligibility?.crypto?.assets || ["USDT"];
  const availableNetworks = eligibility?.crypto?.networks || ["TRC20"];

  const isDisabled = loading || eligibility.loading;
  const isMethodAllowed = availableMethods.includes(method);

  const hasValidMethodData =
    method === "crypto"
      ? Boolean(
          cryptoData.asset.trim() &&
            cryptoData.network.trim() &&
            cryptoData.walletAddress.trim()
        )
      : method === "paypal"
      ? Boolean(paypalEmail.trim())
      : method === "bank"
      ? Boolean(
          bankData.accountHolderName.trim() &&
            bankData.bankName.trim() &&
            bankData.iban.trim() &&
            bankData.country.trim()
        )
      : false;

  const hasValidIdentity =
    identityForm.firstName.trim().length >= 2 &&
    identityForm.lastName.trim().length >= 2 &&
    /^[A-Z]{2}$/i.test(identityForm.payoutCountry.trim()) &&
    identityForm.payoutOwnerConfirmed &&
    (method !== "crypto" || identityForm.cryptoIrreversibleAcknowledged);

  const identityPayload = {
    firstName: identityForm.firstName.trim(),
    lastName: identityForm.lastName.trim(),
    payoutCountry: identityForm.payoutCountry.trim().toUpperCase(),
    payoutOwnerConfirmed: identityForm.payoutOwnerConfirmed === true,
    cryptoIrreversibleAcknowledged:
      method === "crypto"
        ? identityForm.cryptoIrreversibleAcknowledged === true
        : false,
  };

  const canSubmit =
    !isDisabled &&
    eligibility.eligible &&
    isMethodAllowed &&
    requestedUsd > 0 &&
    requestedTokens > 0 &&
    requestedTokens >= toNumber(eligibility.minWithdrawTokens, 1000) &&
    requestedTokens <= toNumber(eligibility.availableTokens, 0) &&
    hasValidIdentity &&
    hasValidMethodData;

  const submitBlockedReason = useMemo(() => {
    if (loading) return "";
    if (eligibility.loading) {
      return t("withdraw.validation.checkingEligibility", {
        defaultValue: "Checking eligibility...",
      });
    }
    if (!eligibility.eligible) {
      return (
        eligibility.reason ||
        t("withdraw.validation.notEligible", {
          defaultValue: "Withdrawal is not available right now.",
        })
      );
    }
    if (!isMethodAllowed) {
      return t("withdraw.validation.methodNotAllowed", {
        defaultValue:
          "The selected receiving method is not allowed. Choose one of the available methods.",
      });
    }
    if (!(requestedUsd > 0)) {
      return t("withdraw.validation.invalidAmount", {
        defaultValue: "Enter a valid withdrawal amount.",
      });
    }
    if (requestedTokens <= 0) {
      return t("withdraw.validation.invalidConvertedAmount", {
        defaultValue: "The amount is too small after conversion to tokens.",
      });
    }
    if (requestedTokens > toNumber(eligibility.availableTokens, 0)) {
      return t("withdraw.validation.amountTooHigh", {
        defaultValue: "The amount is higher than your withdrawable balance.",
      });
    }
    if (requestedTokens < toNumber(eligibility.minWithdrawTokens, 1000)) {
      return t("withdraw.validation.minimumAmount", {
        defaultValue: "Minimum withdrawal amount is about ${{amount}}.",
        amount: formatMoney(toNumber(eligibility.minWithdrawUsdPreview, 10)),
      });
    }
    if (!hasValidIdentity) {
      return t("withdraw.validation.recipientRequired", {
        defaultValue: "Complete recipient information before submitting.",
      });
    }
    if (!hasValidMethodData) {
      if (method === "crypto") {
        return t("withdraw.validation.walletRequired", {
          defaultValue:
            "Complete the currency, network, and valid TRC20 wallet address.",
        });
      }
      if (method === "paypal") {
        return t("withdraw.validation.paypalRequired", {
          defaultValue: "Enter your PayPal email.",
        });
      }
      if (method === "bank") {
        return t("withdraw.validation.bankFieldsRequired", {
          defaultValue: "Complete the required bank account details.",
        });
      }
      return t("withdraw.validation.methodFieldsRequired", {
        defaultValue: "Complete the receiving method details.",
      });
    }
    return "";
  }, [
    loading,
    eligibility.loading,
    eligibility.eligible,
    eligibility.reason,
    eligibility.availableTokens,
    eligibility.minWithdrawTokens,
    eligibility.minWithdrawUsdPreview,
    isMethodAllowed,
    requestedUsd,
    requestedTokens,
    hasValidIdentity,
    hasValidMethodData,
    method,
    t,
  ]);

  const showToast = (message) => {
    setToast(String(message || ""));
  };

  const buildPayload = () => {
    const base = {
      amountTokens: requestedTokens,
      amountUsdPreview: requestedUsd,
      noteFromUser: noteFromUser.trim(),
      ...identityPayload,
    };

    if (method === "crypto") {
      return {
        ...base,
        method: "crypto",
        asset: cryptoData.asset.trim().toUpperCase(),
        network: cryptoData.network.trim().toUpperCase(),
        walletAddress: cryptoData.walletAddress.trim(),
      };
    }

    if (method === "paypal") {
      return {
        ...base,
        method: "paypal",
        paypalEmail: paypalEmail.trim().toLowerCase(),
      };
    }

    return {
      ...base,
      method: "bank",
      accountHolderName: bankData.accountHolderName.trim(),
      bankName: bankData.bankName.trim(),
      iban: bankData.iban.trim().replace(/\s+/g, ""),
      country: bankData.country.trim().toUpperCase(),
      bic: bankData.bic.trim().toUpperCase(),
    };
  };

  const handleSubmit = async () => {
    setSubmitError("");

    // Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: must never call
    // createWithdrawRequest while real-money payouts are disabled.
    if (!ENABLE_REAL_MONEY_PAYOUTS) {
      showToast(
        t("withdraw.validation.payoutsDisabled", {
          defaultValue: "Real-money withdrawal is currently unavailable.",
        })
      );
      return;
    }

    if (!eligibility.eligible) {
      showToast(
        eligibility.reason ||
          t("withdraw.validation.notEligible", {
            defaultValue: "السحب غير متاح حاليًا",
          })
      );
      return;
    }

    if (!requestedUsd || requestedUsd <= 0) {
      showToast(
        t("withdraw.validation.invalidAmount", {
          defaultValue: "أدخلي قيمة السحب بالدولار",
        })
      );
      return;
    }

    if (requestedTokens <= 0) {
      showToast(
        t("withdraw.validation.invalidConvertedAmount", {
          defaultValue: "قيمة السحب المدخلة صغيرة جدًا",
        })
      );
      return;
    }

    if (requestedTokens > toNumber(eligibility.availableTokens, 0)) {
      showToast(
        t("withdraw.validation.amountTooHigh", {
          defaultValue: "المبلغ المطلوب أكبر من الرصيد المتاح للسحب",
        })
      );
      return;
    }

    if (requestedTokens < toNumber(eligibility.minWithdrawTokens, 1000)) {
      showToast(
        t("withdraw.validation.minimumAmount", {
          defaultValue:
            "الحد الأدنى للسحب هو " +
            formatMoney(toNumber(eligibility.minWithdrawUsdPreview, 10)) +
            " دولار",
          amount: formatMoney(toNumber(eligibility.minWithdrawUsdPreview, 10)),
          currency: "USD",
        })
      );
      return;
    }

    if (method === "crypto" && !cryptoData.walletAddress.trim()) {
      showToast(
        t("withdraw.validation.walletRequired", {
          defaultValue: "أدخلي عنوان المحفظة لاستلام السحب",
        })
      );
      return;
    }

    if (method === "paypal" && !paypalEmail.trim()) {
      showToast(
        t("withdraw.validation.paypalRequired", {
          defaultValue: "أدخلي بريد PayPal لاستلام السحب",
        })
      );
      return;
    }

    if (
      method === "bank" &&
      (!bankData.accountHolderName.trim() ||
        !bankData.bankName.trim() ||
        !bankData.iban.trim() ||
        !bankData.country.trim())
    ) {
      showToast(
        t("withdraw.validation.bankFieldsRequired", {
          defaultValue: "أكملي بيانات الحساب البنكي المطلوبة",
        })
      );
      return;
    }

    try {
      setLoading(true);

      const payload = buildPayload();
      console.log("createWithdrawRequest payload", payload);
      const res = await createWithdrawRequest(payload);
      const message =
        res?.data?.message ||
        t("withdraw.status.success", {
          defaultValue: "تم إرسال طلب السحب بنجاح",
        });

      setSubmitError("");
      showToast(message);
      navigate("/withdraw-history");
    } catch (err) {
      console.error("createWithdrawRequest failed", err);

      const code = typeof err?.code === "string" ? err.code : "";
      const msg =
        typeof err?.message === "string" && err.message.trim()
          ? err.message.trim()
          : "";
      const details =
        err?.details != null && typeof err.details === "string"
          ? err.details
          : "";
      const visible =
        [code, details || msg].filter(Boolean).join(" — ") ||
        msg ||
        code ||
        String(err ?? "Unknown error");
      setSubmitError(visible);
      showToast(
        visible ||
          t("withdraw.status.error", {
            defaultValue: "حدث خطأ أثناء إرسال طلب السحب",
          })
      );
    } finally {
      setLoading(false);
    }
  };

  const statusText =
    eligibility.reason ||
    (eligibility.eligible
      ? t("withdraw.status.instantAvailable", {
          defaultValue: "يمكنك إرسال طلب السحب الآن",
        })
      : t("withdraw.status.instantUnavailable", {
          defaultValue: "السحب غير متاح حاليًا",
        }));

  // Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: while real-money payouts are
  // disabled, render a clear unavailable notice only - no method/bank/
  // PayPal/crypto fields, no amount input, no direct-route access to the
  // submission form. The original full form JSX below is preserved
  // unchanged (not deleted) for reactivation later.
  if (!ENABLE_REAL_MONEY_PAYOUTS) {
    return (
      <div className="withdraw-request-page">
        <TopBar2
          title={t("withdraw.request.title", {
            defaultValue: "طلب السحب",
          })}
          onBack={() => navigate(-1)}
        />

        <div className="withdraw-request-page__spacer" />

        <div className="withdraw-request-page__content">
          <div className="withdraw-request-hero">
            <h1 className="withdraw-request-hero__title">
              {t("withdraw.request.title", {
                defaultValue: "طلب السحب",
              })}
            </h1>

            <p className="withdraw-request-hero__subtitle">
              {t("withdraw.validation.payoutsDisabled", {
                defaultValue: "Real-money withdrawal is currently unavailable.",
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="withdraw-request-page">
      <TopBar2
        title={t("withdraw.request.title", {
          defaultValue: "طلب السحب",
        })}
        onBack={() => navigate(-1)}
      />

      <div className="withdraw-request-page__spacer" />

      <div className="withdraw-request-page__content">
        <div className="withdraw-request-hero">
          <h1 className="withdraw-request-hero__title">
            {t("withdraw.request.title", {
              defaultValue: "طلب السحب",
            })}
          </h1>

          <p className="withdraw-request-hero__subtitle">
            {t("withdraw.request.subtitle", {
              defaultValue:
                "أدخلي قيمة السحب بالدولار، وسيتم احتسابها وفق سعر المنصة الداخلي: كل 100 توكن = 1 دولار.",
            })}
          </p>
        </div>

        <div className="withdraw-request-balance-card">
          <div className="withdraw-request-balance-card__label">
            {t("withdraw.balance.available", {
              defaultValue: "الرصيد القابل للسحب",
            })}
          </div>

          <div className="withdraw-request-balance-card__amount">
            {formatNumber(eligibility.availableTokens)}{" "}
            {t("tokens.unit", {
              defaultValue: "توكن",
            })}
          </div>

          <div className="withdraw-request-balance-card__meta">
            {t("withdraw.balance.availableUsdPreview", {
              defaultValue: "القيمة التقريبية الحالية",
            })}{" "}
            (~${formatMoney(eligibility.availableUsdPreview)})
          </div>

          <div className="withdraw-request-balance-card__min">
            {t("withdraw.validation.minimumAmount", {
              defaultValue:
                "الحد الأدنى للسحب: $" +
                formatMoney(eligibility.minWithdrawUsdPreview) +
                " (" +
                formatNumber(eligibility.minWithdrawTokens) +
                " توكن)",
              amount:
                "$" +
                formatMoney(eligibility.minWithdrawUsdPreview) +
                " (" +
                formatNumber(eligibility.minWithdrawTokens) +
                " " +
                t("tokens.unit", { defaultValue: "Token" }) +
                ")",
              currency: "",
            })}
          </div>

          {!eligibility.loading && (
            <div className="withdraw-request-balance-card__warning">
              {statusText}
            </div>
          )}
        </div>

        <div className="withdraw-request-form-card">
          <div className="withdraw-request-field">
            <label className="withdraw-request-label">
              {t("withdraw.form.amountUsd", {
                defaultValue: "المبلغ المطلوب بالدولار",
              })}
            </label>

            <input
              type="number"
              min="0.01"
              step="0.01"
              className="withdraw-request-input"
              value={amountUsd}
              onChange={(e) => setAmountUsd(normalizeDigits(e.target.value))}
              placeholder={t("withdraw.form.amountPlaceholder", {
                defaultValue: "مثال: 20",
              })}
              disabled={isDisabled}
            />

            <div className="withdraw-request-helper">
              {t("withdraw.balance.willDeduct", {
                defaultValue: "يعادل هذا السحب تقريبًا",
              })}{" "}
              {formatNumber(requestedTokens)}{" "}
              {t("tokens.unit", {
                defaultValue: "توكن",
              })}
            </div>
          </div>

          <div className="withdraw-request-summary">
            <div className="withdraw-request-summary__item">
              <span>
                {t("withdraw.balance.willDeduct", {
                  defaultValue: "سيُخصم من رصيدك",
                })}
              </span>
              <strong>
                {formatNumber(requestedTokens)}{" "}
                {t("tokens.unit", {
                  defaultValue: "توكن",
                })}
              </strong>
            </div>

            <div className="withdraw-request-summary__item">
              <span>
                {t("withdraw.balance.remaining", {
                  defaultValue: "الرصيد المتوقع بعد السحب",
                })}
              </span>
              <strong>
                {formatNumber(remainingTokens)}{" "}
                {t("tokens.unit", {
                  defaultValue: "توكن",
                })}{" "}
                (~${formatMoney(remainingUsdPreview)})
              </strong>
            </div>
          </div>

          <div className="withdraw-request-field">
            <label className="withdraw-request-label">
              {t("withdraw.form.method", {
                defaultValue: "اختاري طريقة استلام السحب",
              })}
            </label>

            <div className="withdraw-request-methods">
              {availableMethods.includes("crypto") && (
                <button
                  type="button"
                  className={`withdraw-request-method ${
                    method === "crypto" ? "is-active" : ""
                  }`}
                  onClick={() => setMethod("crypto")}
                  disabled={isDisabled}
                >
                  {t("withdraw.methods.crypto", {
                    defaultValue: "كريبتو",
                  })}
                </button>
              )}

              {availableMethods.includes("paypal") && (
                <button
                  type="button"
                  className={`withdraw-request-method ${
                    method === "paypal" ? "is-active" : ""
                  }`}
                  onClick={() => setMethod("paypal")}
                  disabled={isDisabled}
                >
                  {t("withdraw.methods.paypal", {
                    defaultValue: "باي بال",
                  })}
                </button>
              )}

              {availableMethods.includes("bank") && (
                <button
                  type="button"
                  className={`withdraw-request-method ${
                    method === "bank" ? "is-active" : ""
                  }`}
                  onClick={() => setMethod("bank")}
                  disabled={isDisabled}
                >
                  {t("withdraw.methods.bank", {
                    defaultValue: "تحويل بنكي",
                  })}
                </button>
              )}
            </div>
          </div>

          <div className="withdraw-request-identity-card">
            <div className="withdraw-request-identity-card__title">
              {t("withdraw.recipient.sectionTitle", {
                defaultValue: "بيانات الاستلام",
              })}
            </div>
            <p className="withdraw-request-identity-card__hint">
              {t("withdraw.recipient.sectionHint", {
                defaultValue:
                  "تأكدي من بيانات المستلمة قبل إرسال طلب السحب. يمكنك تعديلها في أي وقت قبل الإرسال.",
              })}
            </p>

            <div className="withdraw-request-grid">
              <div className="withdraw-request-field">
                <label className="withdraw-request-label">
                  {t("withdraw.recipient.firstName", {
                    defaultValue: "الاسم الأول",
                  })}
                </label>
                <input
                  type="text"
                  className="withdraw-request-input"
                  value={identityForm.firstName}
                  onChange={(e) =>
                    setIdentityForm((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  autoComplete="given-name"
                  disabled={isDisabled}
                />
              </div>

              <div className="withdraw-request-field">
                <label className="withdraw-request-label">
                  {t("withdraw.recipient.lastName", {
                    defaultValue: "اسم العائلة",
                  })}
                </label>
                <input
                  type="text"
                  className="withdraw-request-input"
                  value={identityForm.lastName}
                  onChange={(e) =>
                    setIdentityForm((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                  autoComplete="family-name"
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div className="withdraw-request-field">
              <label className="withdraw-request-label">
                {t("withdraw.recipient.payoutCountry", {
                  defaultValue: "دولة الاستلام",
                })}
              </label>
              <select
                className="withdraw-request-select"
                value={identityForm.payoutCountry}
                onChange={(e) =>
                  setIdentityForm((prev) => ({
                    ...prev,
                    payoutCountry: e.target.value,
                  }))
                }
                disabled={isDisabled}
              >
                <option value="">
                  {t("withdraw.recipient.countryPlaceholder", {
                    defaultValue: "اختاري الدولة",
                  })}
                </option>
                {PAYOUT_COUNTRY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label} ({c.code})
                  </option>
                ))}
              </select>
            </div>

            <label className="withdraw-request-check">
              <input
                type="checkbox"
                checked={identityForm.payoutOwnerConfirmed}
                onChange={(e) =>
                  setIdentityForm((prev) => ({
                    ...prev,
                    payoutOwnerConfirmed: e.target.checked,
                  }))
                }
                disabled={isDisabled}
              />
              <span>
                {t("withdraw.recipient.ownerConfirmed", {
                  defaultValue:
                    "أؤكد أن بيانات الاستلام صحيحة وأنني صاحبة الحساب.",
                })}
              </span>
            </label>

            {method === "crypto" ? (
              <label className="withdraw-request-check">
                <input
                  type="checkbox"
                  checked={identityForm.cryptoIrreversibleAcknowledged}
                  onChange={(e) =>
                    setIdentityForm((prev) => ({
                      ...prev,
                      cryptoIrreversibleAcknowledged: e.target.checked,
                    }))
                  }
                  disabled={isDisabled}
                />
                <span>
                  {t("withdraw.recipient.cryptoIrreversible", {
                    defaultValue:
                      "أفهم أن تحويلات الكريبتو لا يمكن عكسها بعد الإرسال.",
                  })}
                </span>
              </label>
            ) : null}
          </div>

          {method === "crypto" && (
            <div className="withdraw-request-method-box">
              <div className="withdraw-request-grid">
                <div className="withdraw-request-field">
                  <label className="withdraw-request-label">
                    {t("withdraw.form.asset", {
                      defaultValue: "العملة الرقمية",
                    })}
                  </label>

                  <select
                    className="withdraw-request-select"
                    value={cryptoData.asset}
                    onChange={(e) =>
                      setCryptoData((prev) => ({
                        ...prev,
                        asset: e.target.value,
                      }))
                    }
                    disabled={isDisabled}
                  >
                    {availableAssets.map((asset) => (
                      <option key={asset} value={asset}>
                        {asset}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="withdraw-request-field">
                  <label className="withdraw-request-label">
                    {t("withdraw.form.network", {
                      defaultValue: "شبكة الاستلام",
                    })}
                  </label>

                  <select
                    className="withdraw-request-select"
                    value={cryptoData.network}
                    onChange={(e) =>
                      setCryptoData((prev) => ({
                        ...prev,
                        network: e.target.value,
                      }))
                    }
                    disabled={isDisabled}
                  >
                    {availableNetworks.map((network) => (
                      <option key={network} value={network}>
                        {network}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="withdraw-request-field">
                <label className="withdraw-request-label">
                  {t("withdraw.form.walletAddress", {
                    defaultValue: "عنوان المحفظة",
                  })}
                </label>

                <input
                  type="text"
                  className="withdraw-request-input"
                  value={cryptoData.walletAddress}
                  onChange={(e) =>
                    setCryptoData((prev) => ({
                      ...prev,
                      walletAddress: e.target.value,
                    }))
                  }
                  placeholder={t("withdraw.form.walletPlaceholder", {
                    defaultValue: "مثال: Txxxxxxxxxxxxxxxx",
                  })}
                  disabled={isDisabled}
                />
              </div>
            </div>
          )}

          {method === "paypal" && (
            <div className="withdraw-request-method-box">
              <div className="withdraw-request-field">
                <label className="withdraw-request-label">
                  {t("withdraw.form.paypalEmail", {
                    defaultValue: "بريد PayPal",
                  })}
                </label>

                <input
                  type="email"
                  className="withdraw-request-input"
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  placeholder={t("withdraw.form.paypalPlaceholder", {
                    defaultValue: "example@email.com",
                  })}
                  disabled={isDisabled}
                />
              </div>
            </div>
          )}

          {method === "bank" && (
            <div className="withdraw-request-method-box">
              <div className="withdraw-request-field">
                <label className="withdraw-request-label">
                  {t("withdraw.form.accountHolderName", {
                    defaultValue: "اسم صاحب الحساب",
                  })}
                </label>

                <input
                  type="text"
                  className="withdraw-request-input"
                  value={bankData.accountHolderName}
                  onChange={(e) =>
                    setBankData((prev) => ({
                      ...prev,
                      accountHolderName: e.target.value,
                    }))
                  }
                  placeholder={t("withdraw.form.accountHolderPlaceholder", {
                    defaultValue: "الاسم الكامل",
                  })}
                  disabled={isDisabled}
                />
              </div>

              <div className="withdraw-request-field">
                <label className="withdraw-request-label">
                  {t("withdraw.form.bankName", {
                    defaultValue: "اسم البنك",
                  })}
                </label>

                <input
                  type="text"
                  className="withdraw-request-input"
                  value={bankData.bankName}
                  onChange={(e) =>
                    setBankData((prev) => ({
                      ...prev,
                      bankName: e.target.value,
                    }))
                  }
                  placeholder={t("withdraw.form.bankNamePlaceholder", {
                    defaultValue: "مثال: Erste Bank",
                  })}
                  disabled={isDisabled}
                />
              </div>

              <div className="withdraw-request-field">
                <label className="withdraw-request-label">
                  {t("withdraw.form.iban", {
                    defaultValue: "رقم IBAN",
                  })}
                </label>

                <input
                  type="text"
                  className="withdraw-request-input"
                  value={bankData.iban}
                  onChange={(e) =>
                    setBankData((prev) => ({
                      ...prev,
                      iban: e.target.value,
                    }))
                  }
                  placeholder={t("withdraw.form.ibanPlaceholder", {
                    defaultValue: "مثال: AT00 0000 0000 0000 0000",
                  })}
                  disabled={isDisabled}
                />
              </div>

              <div className="withdraw-request-grid">
                <div className="withdraw-request-field">
                  <label className="withdraw-request-label">
                    {t("withdraw.form.country", {
                      defaultValue: "رمز الدولة",
                    })}
                  </label>

                  <input
                    type="text"
                    className="withdraw-request-input"
                    value={bankData.country}
                    onChange={(e) =>
                      setBankData((prev) => ({
                        ...prev,
                        country: e.target.value,
                      }))
                    }
                    placeholder={t("withdraw.form.countryPlaceholder", {
                      defaultValue: "مثال: AT",
                    })}
                    disabled={isDisabled}
                  />
                </div>

                <div className="withdraw-request-field">
                  <label className="withdraw-request-label">
                    {t("withdraw.form.bic", {
                      defaultValue: "BIC / SWIFT",
                    })}
                  </label>

                  <input
                    type="text"
                    className="withdraw-request-input"
                    value={bankData.bic}
                    onChange={(e) =>
                      setBankData((prev) => ({
                        ...prev,
                        bic: e.target.value,
                      }))
                    }
                    placeholder={t("withdraw.form.bicPlaceholder", {
                      defaultValue: "اختياري",
                    })}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="withdraw-request-field">
            <label className="withdraw-request-label">
              {t("withdraw.form.note", {
                defaultValue: "ملاحظة إضافية",
              })}
            </label>

            <textarea
              className="withdraw-request-textarea"
              rows={3}
              value={noteFromUser}
              onChange={(e) => setNoteFromUser(e.target.value)}
              placeholder={t("withdraw.form.notePlaceholder", {
                defaultValue: "اختياري",
              })}
              disabled={isDisabled}
            />
          </div>

          {submitError ? (
            <div
              role="alert"
              style={{
                marginBottom: 12,
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid #b91c1c",
                background: "rgba(127, 29, 29, 0.35)",
                color: "#fecaca",
                fontSize: 14,
                lineHeight: 1.45,
              }}
            >
              {submitError}
            </div>
          ) : null}

          <button
            type="button"
            className={`withdraw-request-submit ${
              !canSubmit ? "is-disabled" : ""
            }`}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {loading
              ? t("withdraw.request.sending", {
                  defaultValue: "جارٍ إرسال الطلب...",
                })
              : t("withdraw.request.submit", {
                  defaultValue: "إرسال طلب السحب",
                })}
          </button>

          {!canSubmit && submitBlockedReason ? (
            <p
              style={{
                marginTop: 12,
                fontSize: 13,
                color: "#fca5a5",
                lineHeight: 1.5,
              }}
            >
              {submitBlockedReason}
            </p>
          ) : null}
        </div>
      </div>

      {toast && (
        <div className="withdraw-request-toast" onClick={() => setToast("")}>
          {toast}
        </div>
      )}
    </div>
  );
}
